import React, { useEffect, useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import AllJobsTable from '../AllJobsTable/AllJobsTable';
import {
  allJobRequest,
  deleteJobRequest,
  updateJobRequest,
  getJobRequest,
  createInvoiceRequest,
  createExpensesRequest,
  updateExpensesRequest,
  updateInvoiceRequest,
} from '../../actions';

/** Match `QuotationsPage`: sidebar offset + full remaining viewport width for the table. */
const useStyles = makeStyles((theme) => ({
  introBox: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '130px',
    marginTop: '60px',
    fontFamily: 'Roboto Mono, monospace',
  },
  primaryText: { fontSize: '35px', fontWeight: 'bold' },
  secondaryText: { position: 'relative', top: '-10px', opacity: '.78' },
  tableWrap: {
    marginLeft: '130px',
    marginTop: theme.spacing(2),
    width: 'calc(100vw - 100px - 130px - 16px)',
    maxWidth: 'none',
    boxSizing: 'border-box',
    overflowX: 'auto',
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(4),
  },
}));

const AllJobsPage = ({ match }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const highlightedJobNumber = React.useMemo(() => {
    const value = new URLSearchParams(location.search).get('highlight');
    return value ? String(value) : '';
  }, [location.search]);


  const [isChanged, updateIsChanged] = useState(true);
  const dispatch = useDispatch();
  const { allJobs } = useSelector((state) => state.Job);
  const loading = useSelector((state) => state.Loader);

  const onDeleteJob = useCallback((selectedGcnNo) => {
    updateIsChanged(true);
    dispatch(deleteJobRequest(selectedGcnNo));
  }, [dispatch]);

  const onUpdateJobTracking = useCallback((payload, gcnNo) => {
    updateIsChanged(true);
    dispatch(updateJobRequest(payload, gcnNo));
  }, [dispatch]);

  useEffect(() => {
    if (!isChanged) {
      dispatch(allJobRequest());
    }
    updateIsChanged(false);
  }, [dispatch, isChanged]);

  const data = React.useMemo(() => {
    return allJobs;
  }, [allJobs]);

  const onEditJobClick = useCallback((gcnno) => {
    updateIsChanged(true);
    dispatch(getJobRequest(gcnno));
    history.push(`${match.path}/edit/${gcnno}`);
  }, [dispatch, history, match.path]);

  const onAddInvoiceClick = useCallback((data, gcnno, billno, invoiceDate, shouldUpdate) => {
    updateIsChanged(true);
    if (shouldUpdate) {
      dispatch(updateInvoiceRequest({ invoice: data, gcnno, billno, invoiceDate }));
    } else {
      dispatch(createInvoiceRequest({ invoice: data, gcnno, billno, invoiceDate }));
    }
  }, [dispatch]);

  const onAddExpenseClick = useCallback((data, gcnno, shouldUpdate) => {
    updateIsChanged(true);
    if (shouldUpdate) {
      dispatch(updateExpensesRequest({ expense: data, gcnno }));
    } else {
      dispatch(createExpensesRequest({ expense: data, gcnno }));
    }
  }, [dispatch]);

  return (
    <>
      <Box className={classes.introBox}>
        <Typography variant='h3' component='h1' className={classes.primaryText}>
          All jobs
        </Typography>
        <Typography variant='h6' component='span' className={classes.secondaryText}>
          {/* subtitle removed — table is self-explanatory */}
        </Typography>
      </Box>
      <Box className={classes.tableWrap}>
        <AllJobsTable
          data={data}
          highlightedJobNumber={highlightedJobNumber}
          onDeleteJob={(gcnNo) => onDeleteJob(gcnNo)}
          match={match}
          onUpdateJobTracking={onUpdateJobTracking}
          onEditJobClick={onEditJobClick}
          isLoading={loading}
          onAddInvoiceClick={onAddInvoiceClick}
          onAddExpenseClick={onAddExpenseClick}
        />
      </Box>
    </>
  );
};

export default AllJobsPage;
